'use client';

import { c as _c } from "react/compiler-runtime";
import type { I18n } from '@funkey/locales';
import { cx } from 'class-variance-authority';
import { LuTruck } from 'react-icons/lu';
import { Icon } from '~/components/primitives';
import { HoverTooltip } from './HoverTooltip';
import { useI18n } from '~/features/i18n';
import { Units } from '~/modules/units';
import type { TransportBreakdownEntry, TransportEstimate } from '../utils/calcTransportCost';

/** Translates + formats one structured breakdown entry for the active locale. */
function breakdownLabel(i18n: I18n, entry: TransportBreakdownEntry): string {
  switch (entry.kind) {
    case 'distanceRate':
      return i18n._('pdp.location.transport.breakdown.distance_rate', {
        km: Math.round(entry.distanceKm),
        rate: Units.formatEUR(Math.round(entry.costPerKm * 100))
      }, {
        message: '{km} km × {rate}/km'
      });
    // prettier-ignore
    case 'roundTrip':
      return i18n._('pdp.location.transport.breakdown.round_trip', {
        km: Math.round(entry.oneWayKm)
      }, {
        message: '({km} km there and back)'
      });
    // prettier-ignore
    case 'extraCost':
      return i18n._('pdp.location.transport.breakdown.extra_cost', {
        amount: Units.formatEUR(Math.round(entry.extraEUR * 100))
      }, {
        message: '+ {amount} fixed fee'
      });
    // prettier-ignore
    case 'perParticipants':
      return entry.multiplier > 1 ? i18n._('pdp.location.transport.breakdown.per_participants', {
        multiplier: entry.multiplier,
        batchSize: entry.batchSize
      }, {
        message: '× {multiplier} (per {batchSize} participants)'
      }) // prettier-ignore
      : i18n._('pdp.location.transport.breakdown.per_participants_single', {
        batchSize: entry.batchSize
      }, {
        message: 'per {batchSize} participants'
      });
    // prettier-ignore
  }
}

/**
 * Marks where a value sits inside a translated sentence. Translations reorder the
 * placeholders (FR puts the city last), so the emphasised parts are located by splitting
 * on these markers rather than by searching for the value itself — a city or amount could
 * otherwise appear twice, or not literally at all.
 */
const MARKER = '\u0000';
const mark = (name: string) => `${MARKER}${name}${MARKER}`;
function emphasise(sentence: string, values: Record<string, string>): React.ReactNode[] {
  return sentence.split(MARKER).map((part, index) =>
  // Odd chunks sit between two markers, i.e. they are the placeholder names.
  index % 2 === 1 ? <strong key={index} className="font-semibold">
                {values[part] ?? ''}
            </strong> : part);
}

/**
 * Custom-mode ("Aanbieder komt naar mij") transport estimate for the selected partner: one
 * sentence with the city and the round-trip amount, and the calculation behind it tucked
 * into the info tooltip. The amount is an estimate (haversine-based unless a Mapbox driving
 * route is available), rendered with the approximate glyph.
 */
export const TransportCostCard: React.FC<{
  /** City the partner travels to (the user's location label). */
  userLabel: string | null;
  transport: TransportEstimate;
  className?: string;
}> = props => {
  const $ = _c(48);
  const i18n = useI18n();
  if (!props.transport.hasTariff) {
    let t0;
    if ($[0] !== props.className) {
      t0 = cx("flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3", props.className);
      $[0] = props.className;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LuTruck className="size-4 shrink-0 text-blue-900/40" />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== i18n) {
      t2 = emphasise(i18n._("pdp.location.transport.included", {
        amount: mark("amount")
      }, {
        message: "This provider charges no transport costs: {amount}"
      }), {
        amount: Units.formatEUR(0)
      });
      $[3] = i18n;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t2) {
      t3 = <p className="text-sm text-blue-900">{t2}</p>;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t0 || $[8] !== t3) {
      t4 = <div className={t0} data-testid="pdp-location-transport-card" data-transport="none">{t1}{t3}</div>;
      $[7] = t0;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (props.transport.isFallback) {
    let t0;
    if ($[10] !== props.className) {
      t0 = cx("text-sm text-blue-900/60", props.className);
      $[10] = props.className;
      $[11] = t0;
    } else {
      t0 = $[11];
    }
    let t1;
    if ($[12] !== i18n) {
      t1 = i18n._("pdp.location.transport.on_request", undefined, {
        message: "The transport cost for this provider follows in your quote."
      });
      $[12] = i18n;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    let t2;
    if ($[14] !== t0 || $[15] !== t1) {
      t2 = <p className={t0} data-testid="pdp-location-transport-card">{t1}</p>;
      $[14] = t0;
      $[15] = t1;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    return t2;
  }
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[17] !== i18n || $[18] !== props.className || $[19] !== props.transport.costEUR || $[20] !== props.userLabel) {
    const amount = props.transport.costEUR === 0 ? i18n._("pdp.location.transport.free", undefined, {
      message: "Free"
    }) : `≈ ${Units.formatEUR(Math.round(props.transport.costEUR * 100))}`;
    const sentence = props.userLabel ? i18n._("pdp.location.transport.sentence", {
      city: mark("city"),
      amount: mark("amount")
    }, {
      message: "Transport costs to {city} amount to {amount}"
    }) : i18n._("pdp.location.transport.sentence_no_city", {
      amount: mark("amount")
    }, {
      message: "Transport costs to your location amount to {amount}"
    });
    if ($[25] !== props.className) {
      t2 = cx("flex items-center justify-between gap-3 rounded-2xl border border-gray-200 px-4 py-3", props.className);
      $[25] = props.className;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    t3 = "pdp-location-transport-card";
    t0 = "text-sm text-blue-900";
    t1 = emphasise(sentence, {
      city: props.userLabel ?? "",
      amount
    });
    $[17] = i18n;
    $[18] = props.className;
    $[19] = props.transport.costEUR;
    $[20] = props.userLabel;
    $[21] = t0;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
  } else {
    t0 = $[21];
    t1 = $[22];
    t2 = $[23];
    t3 = $[24];
  }
  let t4;
  if ($[27] !== t0 || $[28] !== t1) {
    t4 = <p className={t0}>{t1}</p>;
    $[27] = t0;
    $[28] = t1;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  let t5;
  if ($[30] !== i18n || $[31] !== props.transport.breakdown) {
    t5 = !!props.transport.breakdown.length && <ul className="flex flex-col gap-0.5">{props.transport.breakdown.map(entry => <li key={entry.kind}>{breakdownLabel(i18n, entry)}</li>)}</ul>;
    $[30] = i18n;
    $[31] = props.transport.breakdown;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== i18n) {
    t6 = i18n._("pdp.location.transport.estimate_note", undefined, {
      message: "Estimate \u2014 the exact transport cost follows in your quote."
    });
    $[33] = i18n;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== t6) {
    t7 = <p className="opacity-70">{t6}</p>;
    $[35] = t6;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== t5 || $[38] !== t7) {
    t8 = <div className="flex flex-col gap-1 text-left">{t5}{t7}</div>;
    $[37] = t5;
    $[38] = t7;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon name="info-circle" size="xs" className="shrink-0 text-blue-900/40" data-testid="pdp-location-transport-info" />;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== t8) {
    t10 = <HoverTooltip as="div" triggerClassName="shrink-0" content={t8} position="top">{t9}</HoverTooltip>;
    $[41] = t8;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4) {
    t11 = <div className={t2} data-testid={t3}>{t4}{t10}</div>;
    $[43] = t10;
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  return t11;
};